import { Link2Off, PackageCheck } from 'lucide-react';
import { useState } from 'react';
import type { Product } from '@sirajoptic/shared';
import { Combobox, type OptionCombobox } from '@/components/ui/combobox';
import { useProduitsPourSelection } from '@/features/stock/api/stock.api';
import { useAntiRebond } from '@/lib/anti-rebond';
import { dh, entier } from '@/lib/format';

/**
 * Choix de la monture, au stock ou à la main.
 *
 * Les deux voies restent ouvertes, et c'est délibéré. Une monture prise dans la
 * vitrine se choisit dans la liste : la commande garde alors sa référence, et
 * l'exemplaire sort du stock à la livraison (règle 11), sans que personne ait à
 * y penser. Une monture commandée chez le fournisseur, ou reprise d'une
 * ancienne paire, s'écrit simplement — c'est aussi ce que font les dix mille
 * lignes importées du cahier, qui n'ont jamais eu de fiche produit.
 *
 * **Retaper le nom détache la fiche.** C'est le point délicat : si le texte
 * changeait sans rompre le lien, le bon porterait un modèle et le stock en
 * décompterait un autre. Mieux vaut perdre le rattachement — qui se voit, la
 * pastille disparaît — qu'un écart d'inventaire, qui ne se voit pas.
 */
export function MontureInput({
  valeur,
  productId,
  onChangeTexte,
  onChoisirProduit,
  erreur,
}: {
  valeur: string;
  /** Fiche produit rattachée, `null` en saisie libre. */
  productId: string | null;
  onChangeTexte: (texte: string) => void;
  /** Reçoit le produit choisi : le nom, le prix de vente et la référence. */
  onChoisirProduit: (produit: Product) => void;
  erreur?: string;
}) {
  const [recherche, setRecherche] = useState('');

  /* Une requête par lettre balaierait le stock à chaque frappe, comme le
   * faisaient la palette et l'autocomplétion client avant leur correction. */
  const rechercheDifferee = useAntiRebond(recherche);
  const { data, isFetching } = useProduitsPourSelection(rechercheDifferee, 'MONTURE');

  const options: OptionCombobox<Product>[] = (data?.data ?? []).map((p) => ({
    cle: p.id,
    libelle: p.nom,
    /* La quantité d'abord : c'est elle qui dit si la monture est en vitrine ou
     * s'il faudra la commander. */
    description: `${entier(p.quantite)} en stock · ${dh(p.prix_vente)}`,
    valeur: p,
  }));

  return (
    <div>
      <Combobox<Product>
        label="Monture"
        erreur={erreur}
        valeur={valeur}
        chargement={isFetching || recherche !== rechercheDifferee}
        options={options}
        placeholder="Choisir dans le stock, ou écrire le modèle"
        videMessage={
          recherche.trim().length < 2
            ? 'Tapez pour chercher dans le stock, ou écrivez le modèle'
            : 'Aucune monture en stock sous ce nom — le texte sera gardé tel quel'
        }
        onChangeTexte={(texte) => {
          setRecherche(texte);
          onChangeTexte(texte);
        }}
        onSelectionner={(option) => {
          setRecherche('');
          onChoisirProduit(option.valeur);
        }}
      />

      {productId ? (
        <p className="mt-1 flex items-center gap-1.5 text-xs text-success">
          <PackageCheck aria-hidden className="h-3.5 w-3.5 shrink-0" />
          <span>Rattachée au stock — un exemplaire sortira à la livraison.</span>
        </p>
      ) : valeur.trim() ? (
        <p className="mt-1 flex items-center gap-1.5 text-xs text-ink-muted">
          <Link2Off aria-hidden className="h-3.5 w-3.5 shrink-0" />
          <span>Saisie libre — le stock ne bougera pas.</span>
        </p>
      ) : null}
    </div>
  );
}
